<script setup lang="ts">
const { t: $t } = useI18n()

import TicketScannerTicket from '~/components/pages/home/ticket-scanner/TicketScannerTicket.vue'
import TicketScannerResult from '~/components/pages/home/ticket-scanner/TicketScannerResult.vue'

defineOptions({ name: 'TicketScannerDevice' })

interface Props {
	isScanning?: boolean
	isFlipped?: boolean
	showFeatures?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	isScanning: false,
	isFlipped: false,
	showFeatures: false,
})

const emit = defineEmits(['restart'])

const features = [
	{ icon: 'heroicons:bell', title: $t('Automatic Alerts'), desc: $t('Know When You Win') },
	{ icon: 'heroicons:bolt', title: $t('Instant Results'), desc: $t('Scan in Under 3 Seconds') },
	{ icon: 'heroicons:shield-check', title: $t('AI Verified'), desc: $t('98%+ OCR accuracy') },
]
</script>

<template>
	<div class="flex justify-center">
		<div class="relative flex items-start gap-0">
			<!-- Feature cards (desktop) -->
			<Transition name="features">
				<div
					v-if="props.showFeatures"
					class="flex flex-col gap-3 pt-20 z-0 -mr-6"
				>
					<div
						v-for="(feat, i) in features"
						:key="feat.title"
						class="feature-card bg-white border border-gray-200 rounded-l-2xl py-1.5 smm:py-2.5 px-2.5 smm:px-3 space-y-1.5 smm:space-y-2 w-36 smm:w-48"
						:style="{ animationDelay: `${i * 180}ms` }"
					>
						<UIcon
							:name="feat.icon"
							class="size-4 text-green-500"
						/>

						<p class="text-[10px] smm:text-sm text-[#303030] leading-5 font-semibold">{{ feat.title }}</p>
						<p class="text-[8px] smm:text-xs text-[#8D8989] leading-4">{{ feat.desc }}</p>
					</div>
				</div>
			</Transition>

			<!-- Ticket con brackets -->
			<div
				class="relative z-10 w-52 smm:w-72"
				style="perspective: 1200px"
			>
				<span class="bracket top-0 left-0 border-t-2 border-l-2 rounded-tl-xl" />
				<span class="bracket top-0 right-0 border-t-2 border-r-2 rounded-tr-xl" />
				<span class="bracket bottom-0 left-0 border-b-2 border-l-2 rounded-bl-xl" />
				<span class="bracket bottom-0 right-0 border-b-2 border-r-2 rounded-br-xl" />

				<div
					class="ticket-flipper"
					:class="{ flipped: props.isFlipped, 'p-3 smm:p-0': props.isScanning }"
				>
					<!-- 	:class="{ 'max-smm:p-3': props.isScanning }" -->
					<div class="ticket-face front smm:p-3">
						<TicketScannerTicket :is-scanning="props.isScanning" />
					</div>
					<div
						class="ticket-face back smm:p-3"
						:class="{ 'max-smm:p-3': props.isFlipped }"
					>
						<TicketScannerResult @restart="emit('restart')" />
					</div>
				</div>
			</div>
		</div>

		<!-- Feature cards (mobile) -->
		<!-- <Transition name="features">
			<div
				v-if="showFeatures"
				class="flex md:hidden flex-col gap-3 mt-6 w-full px-2"
			>
				<div
					v-for="(feat, i) in features"
					:key="feat.title + '-m'"
					class="feature-card bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm"
					:style="{ animationDelay: `${i * 180}ms` }"
				>
					<div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
						<UIcon
							:name="feat.icon"
							class="w-4 h-4 text-primary"
						/>
					</div>
					<div>
						<p class="text-sm font-semibold text-gray-900 dark:text-white">{{ feat.title }}</p>
						<p class="text-[10px] text-gray-500 dark:text-gray-400">{{ feat.desc }}</p>
					</div>
				</div>
			</div>
		</Transition> -->
	</div>
</template>

<style lang="postcss">
/* ---- Flip mechanic ---- */
.ticket-flipper {
	transform-style: preserve-3d;
	transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
	grid-template-rows: auto;
	@apply w-auto smm:w-72 grid;
}

.ticket-flipper.flipped {
	transform: rotateY(180deg);
}

.ticket-face {
	grid-area: 1 / 1;
	backface-visibility: hidden;
	-webkit-backface-visibility: hidden;
	width: 100%;
	align-self: start;
}

.ticket-face.back {
	transform: rotateY(180deg);
}

/* ---- Ticket card ---- */
.ticket-card {
	background: #1c1b27;
	@apply w-full rounded-xl;
}

.dark .ticket-card {
	background: #111827;
	border-color: #374151;
}

/* ---- Scan line ---- */
@keyframes scanLine {
	0% {
		top: 12px;
		opacity: 0;
	}
	5% {
		opacity: 1;
	}
	90% {
		opacity: 1;
	}
	95% {
		top: calc(100% - 14px);
		opacity: 0;
	}
	100% {
		top: 12px;
		opacity: 0;
	}
}

.scan-line {
	position: absolute;
	left: 14px;
	right: 14px;
	height: 2px;
	border-radius: 2px;
	background: linear-gradient(90deg, transparent 0%, #22c55e 25%, #4ade80 50%, #22c55e 75%, transparent 100%);
	box-shadow: 0 0 18px 5px rgba(34, 197, 94, 0.45);
	animation: scanLine 2.2s ease-in-out infinite;
	pointer-events: none;
	z-index: 10;
}

/* ---- Corner brackets ---- */
.bracket {
	@apply absolute w-9 h-9 border-green-500 border-solid pointer-events-none;
}

.bracket-tl {
	top: 0;
	left: 0;
	border-width: 2px 0 0 2px;
	border-radius: 6px 0 0 0;
}
.bracket-tr {
	top: 0;
	right: 0;
	border-width: 2px 2px 0 0;
	border-radius: 0 6px 0 0;
}
.bracket-bl {
	bottom: 0;
	left: 0;
	border-width: 0 0 2px 2px;
	border-radius: 0 0 0 6px;
}
.bracket-br {
	bottom: 0;
	right: 0;
	border-width: 0 2px 2px 0;
	border-radius: 0 0 6px 0;
}

/* ---- Number balls ---- */
.ball-outline {
	border: 1.5px solid #d1d5db;
	color: #6b7280;
	background: transparent;
}
.ball-outline-gold {
	border: 1.5px solid #d97706;
	color: #d97706;
	background: transparent;
}

.dark .ball-outline {
	border-color: #4b5563;
	color: #9ca3af;
}

/* ---- Feature cards animation ---- */
@keyframes slideInRight {
	from {
		opacity: 0;
		transform: translateX(40px) scale(0.9);
	}
	to {
		opacity: 1;
		transform: translateX(0) scale(1);
	}
}

@keyframes slideInUp {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.feature-card {
	opacity: 0;
	animation: slideInRight 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@media (max-width: 767px) {
	.feature-card {
		animation-name: slideInUp;
	}
}

/* Vue transition for the whole group */
.features-enter-active {
	transition: none;
}
.features-leave-active {
	transition: opacity 0.3s ease;
}
.features-leave-to {
	opacity: 0;
}
</style>
